/* AI features: tenant extract/chat (deterministic mock, mirrors api/internal/ai) + platform admin AI settings. */
import { addDays } from "../../domain/dates";
import type * as T from "../types";
import { E, has, isInt, isNum, str, type Ctx } from "./ctx";
import { tenantContext, type TenantContext } from "./derived";

export const MOCK_MODEL = "rukn-mock";

/** FNV-1a 32-bit over UTF-8 bytes (same as Go hash/fnv New32a). */
export function fnv32a(s: string): number {
  let h = 0x811c9dc5;
  for (const b of new TextEncoder().encode(s)) {
    h ^= b;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}
export const confidence = (feature: string, sample: string) => (80 + (fnv32a(feature + "|" + sample) % 20)) / 100;

type Fields = Record<string, string | number | boolean | null>;

export function mockExtract(feature: string, sampleIn: string | undefined, tc: Partial<TenantContext>): { fields: Fields; confidence: number } {
  const sample = sampleIn || "default";
  const v = sample === "default" ? 0 : fnv32a(sample) % 3;
  const today = tc.today || "2026-10-03";
  const conf = confidence(feature, sample);
  const r = (fields: Fields) => ({ fields, confidence: conf });
  switch (feature) {
    case "idscan":
      return r([
        { nameEn: "Rajesh Nair", nameAr: "راجيش ناير", idNumber: "2000815657", nationality: "IN", dob: "1994-08-13", expiry: "2026-11-13" },
        { nameEn: "Faisal Al-Qahtani", nameAr: "فيصل القحطاني", idNumber: "1098765432", nationality: "SA", dob: "1990-03-21", expiry: "2031-05-10" },
        { nameEn: "Maria Santos", nameAr: "ماريا سانتوس", idNumber: "2456789012", nationality: "PH", dob: "1988-12-02", expiry: "2027-02-28" },
      ][v]);
    case "receipt":
      return r({
        amount: tc.expectedInvoice || 1333.85, date: today, ref: v === 2 ? "" : tc.nextPaymentRef ?? "", sender: tc.tenantName || "Customer",
        bank: ["Al Rajhi Bank", "Saudi National Bank (SNB)", "Riyad Bank"][v],
      });
    case "expense":
      return r([
        { vendor: "Jarir Bookstore", vatNo: "300012345600003", date: today, amount: 862.5, vat: 112.5, category: "Office supplies" },
        { vendor: "Uber · Riyadh", vatNo: "310123456700003", date: today, amount: 146, vat: 19.04, category: "Transport" },
        { vendor: "Al Baik", vatNo: "300987654300003", date: today, amount: 318, vat: 41.48, category: "Client entertainment" },
      ][v]);
    case "leave": {
      const from = addDays(today, 9 + v);
      return r({ type: ["Annual", "Sick", "Annual"][v], from, to: addDays(from, [14, 2, 4][v]), days: [15, 3, 5][v], note: ["Family visit", "Medical report attached", "Personal matters"][v] });
    }
    case "import":
      return r({ mapped: [14, 12, 9][v], columns: [16, 14, 12][v] });
    case "letter":
    case "letters":
      return r({ body: `To whom it may concern,\n\nThis is to certify that the bearer is employed by ${tc.tenantName || "our company"}. This letter is issued upon the employee's request without any liability on the company.\n\nHR Department` });
    case "contract":
      return r([
        { basic: 9800, housing: 2450, transport: 980, title: "Procurement Officer", start: "2021-06-01" },
        { basic: 12000, housing: 3000, transport: 1200, title: "Senior Accountant", start: addDays(today, 30) },
        { basic: 6500, housing: 1625, transport: 650, title: "Sales Executive", start: addDays(today, 14) },
      ][v]);
  }
  return r({ summary: `Reviewed ${feature} input (${sample}): no issues found.` });
}

const any = (s: string, ...ws: string[]) => ws.some((w) => s.includes(w));

export function mockChat(msg: string, lang: string, tc: TenantContext): string {
  const m = msg.toLowerCase();
  const ar = lang === "ar";
  const f2 = (n: number) => n.toFixed(2);
  if (any(m, "headcount", "how many employees", "employees", "staff", "موظف", "عدد"))
    return ar ? `لدى ${tc.tenantName} حاليًا ${tc.headcount} موظفًا نشطًا.` : `${tc.tenantName} currently has ${tc.headcount} active employees.`;
  if (any(m, "payroll", "salary", "salaries", "wps", "رواتب", "راتب", "مسير"))
    return ar ? `إجمالي رواتب ${tc.payrollPeriod} هو ${f2(tc.payrollGross)} ${tc.currencyAr || "ريال"} (الصافي ${f2(tc.payrollNet)} ${tc.currencyAr || "ريال"}).` : `Payroll ${tc.payrollPeriod}: gross ${tc.currency || "SAR"} ${f2(tc.payrollGross)}, net ${tc.currency || "SAR"} ${f2(tc.payrollNet)}.`;
  if (any(m, "expir", "iqama", "document", "passport", "وثائق", "إقامة", "اقامة", "تنتهي"))
    return ar ? `هناك ${tc.expiringDocs} وثيقة تنتهي خلال 90 يومًا.` : `${tc.expiringDocs} documents expire within the next 90 days.`;
  if (any(m, "saudi", "nitaqat", "saudization", "سعودة", "نطاقات"))
    return ar ? `نسبة السعودة ${f2(tc.saudizationPct)}% (النطاق: ${tc.saudizationBand}).` : `Saudization is ${f2(tc.saudizationPct)}% (${tc.saudizationBand}).`;
  if (any(m, "leave", "vacation", "إجاز", "اجاز"))
    return ar ? `يوجد ${tc.pendingLeaves} طلب إجازة بانتظار الموافقة.` : `There are ${tc.pendingLeaves} leave requests pending approval.`;
  return ar
    ? "يمكنني مساعدتك في: عدد الموظفين، الرواتب، الوثائق المنتهية، السعودة، وطلبات الإجازة."
    : 'I can help with headcount, payroll totals, expiring documents, Saudization and pending leave requests. Try: "How many employees do we have?"';
}

/** first 6 + "••••••••" + last 4 (Go secret.MaskKey). */
export const maskKey = (k: string) => (k.length < 10 ? "••••••••" : k.slice(0, 6) + "••••••••" + k.slice(-4));

export function aiOps(c: Ctx) {
  const pl = () => c.state.aiPlatform;
  const cfg = (used: number, limit: number, features: T.AiFeature[]): T.AiConfig => ({
    providers: pl().providers, residency: pl().residency, budget: pl().budget, credits: pl().credits, used, limit, features,
  });
  const allTenants = () => [...c.tenantStates.values()];
  const adminCfg = () => cfg(allTenants().reduce((n, t) => n + t.aiTenant.used, 0), allTenants().reduce((n, t) => n + t.aiTenant.limit, 0), pl().features);
  const charge = (featureId: string): T.AiFeature => {
    const a = c.state.aiTenant;
    const f = a.features.find((x) => x.id === featureId);
    if (!f) throw E.field("feature", "unknown feature");
    if (!f.enabled) throw E.conflict("AI feature is disabled");
    if (a.used >= a.limit) throw E.conflict("AI credit limit reached");
    a.used++;
    f.uses++;
    const ts = c.state.tenants.find((t) => t.id === c.tid);
    if (ts) ts.aiCredits = a.used;
    return f;
  };
  const provider = (id: string) => {
    const p = pl().providers.find((x) => x.id === id);
    if (!p) throw E.notFound("provider not found");
    return p;
  };

  return {
    async aiFeatures(): Promise<T.AiConfig> {
      c.principal("ai.read");
      return cfg(c.state.aiTenant.used, c.state.aiTenant.limit, c.state.aiTenant.features);
    },

    async updateAiFeature(id: string, patch: Partial<Pick<T.AiFeature, "enabled" | "model">>): Promise<T.AiFeature> {
      const u = c.principal("ai.update");
      const f = c.state.aiTenant.features.find((x) => x.id === id);
      if (!f) throw E.notFound("feature not found");
      const fe: Record<string, string> = {};
      const next = { ...f };
      if (has(patch, "enabled")) { if (typeof patch.enabled !== "boolean") fe.enabled = "must be true or false"; else next.enabled = patch.enabled; }
      if (has(patch, "model")) {
        const m = str(patch.model);
        if (!pl().providers.some((p) => p.models.includes(m))) fe.model = "unknown model"; else next.model = m;
      }
      if (Object.keys(fe).length) throw E.validation(fe);
      Object.assign(f, next);
      c.audit(u, "Updated AI feature", "AIFeature", id, Object.keys(patch || {}).sort().join(", "));
      return f;
    },

    async aiExtract(req: T.AiExtractRequest): Promise<T.AiExtractResponse> {
      const u = c.principal("ai.extract");
      const feature = str(req?.feature);
      if (!feature) throw E.field("feature", "required");
      if (c.isEmployee(u) && feature !== "leave" && feature !== "expense") throw E.forbidden("employees may only use leave and expense extraction");
      if (req.fileKey && !c.ownsKey(u, req.fileKey)) throw E.forbidden("file belongs to another tenant");
      if (str(req.text).length > 20000) throw E.field("text", "max 20000 characters");
      const f = charge(feature);
      const { fields, confidence: conf } = mockExtract(f.id, req.sample, tenantContext(c));
      c.audit(u, "Used AI extraction", "AIFeature", f.id, MOCK_MODEL);
      return { feature: f.id, fields, confidence: conf, model: MOCK_MODEL, creditsUsed: 1 };
    },

    async aiChat(req: T.AiChatRequest): Promise<T.AiChatResponse> {
      const u = c.principal("ai.chat");
      const msg = str(req?.message);
      if (!msg.trim()) throw E.field("message", "required");
      if (msg.length > 4000) throw E.field("message", "max 4000 characters");
      const lang = req.lang || "en";
      if (lang !== "en" && lang !== "ar") throw E.field("lang", "must be en or ar");
      const f = charge("assistant");
      const reply = mockChat(msg, lang, tenantContext(c));
      c.audit(u, "Used AI assistant", "AIFeature", f.id, MOCK_MODEL);
      return { reply, model: MOCK_MODEL, creditsUsed: 1 };
    },

    async getAdminAi(): Promise<T.AiConfig> {
      c.principal("admin");
      return adminCfg();
    },

    async updateAdminAi(patch: Partial<Pick<T.AiConfig, "residency" | "budget" | "credits">>): Promise<T.AiConfig> {
      const u = c.principal("admin");
      const fe: Record<string, string> = {};
      const next = structuredClone(pl());
      let creditsChanged = false;
      if (has(patch, "residency")) { if (typeof patch.residency !== "boolean") fe.residency = "must be true or false"; else next.residency = patch.residency; }
      if (has(patch, "budget")) { if (!isNum(patch.budget) || patch.budget < 0) fe.budget = "must be ≥ 0"; else next.budget = patch.budget; }
      if (has(patch, "credits")) {
        if (typeof patch.credits !== "object" || Array.isArray(patch.credits)) throw E.field("credits", "must be an object");
        for (const [k, v] of Object.entries(patch.credits!)) {
          if (!(k in c.state.planPrices)) { fe["credits." + k] = "unknown plan"; continue; }
          if (!isInt(v) || v < 0) { fe["credits." + k] = "must be an integer ≥ 0"; continue; }
          next.credits[k] = v;
          creditsChanged = true;
        }
      }
      if (Object.keys(fe).length) throw E.validation(fe);
      c.state.aiPlatform = next;
      if (creditsChanged) {
        for (const t of allTenants()) {
          const lim = next.credits[t.subscription.plan];
          if (lim != null) t.aiTenant.limit = lim;
        }
      }
      c.audit(u, "Updated AI settings", "AI", "", Object.keys(patch || {}).sort().join(", "));
      return adminCfg();
    },

    async connectAiProvider(id: string, apiKey: string, model?: string): Promise<T.AiConfig> {
      const u = c.principal("admin");
      const p = provider(id);
      const key = str(apiKey).trim();
      if (key.length < 8) throw E.field("apiKey", "must be at least 8 characters");
      p.keyMasked = maskKey(key);
      p.connected = true;
      const mdl = str(model).trim();
      if (mdl && !p.models.includes(mdl)) {
        const j = p.models.indexOf("set-model-id");
        if (j >= 0) {
          p.models[j] = mdl;
          if ("set-model-id" in p.pricePerMTok) { p.pricePerMTok[mdl] = p.pricePerMTok["set-model-id"]; delete p.pricePerMTok["set-model-id"]; }
        } else p.models.push(mdl);
      }
      c.audit(u, "Connected AI provider", "AIProvider", p.id, p.keyMasked);
      return adminCfg();
    },

    async disconnectAiProvider(id: string): Promise<T.AiConfig> {
      const u = c.principal("admin");
      const p = provider(id);
      p.keyMasked = "";
      p.connected = false;
      c.audit(u, "Disconnected AI provider", "AIProvider", p.id, "");
      return adminCfg();
    },
  };
}
